import { Helmet, HelmetData } from "react-helmet-async";
import {
  AI_BG_NEW,
  // Blog_Main_Img,
} from "../../../assets";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { client } from "../../../client";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../../utils/animations";
const helmetData = new HelmetData({});

export default function BlogsNewPage() {
  const pageRef = useRef(null);
  const recentRef = useRef(null);
  const glowRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [length, setLength] = useState(6);
  const [featuredPost, setFeaturedPost] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    client
      .fetch(
        `*[_type == "Title"]{
        title,
        author,
        body,
        author_avatar,
        cover_photo,
        tags,
        published_date,
        featured_post,
        id,
        is_global,
        news,
        external_link
      }`
      )
      .then((data) => {
        const sortedData = [...data].sort((a, b) => {
          const dateA = a.published_date ? new Date(a.published_date) : new Date(0);
          const dateB = b.published_date ? new Date(b.published_date) : new Date(0);
          return dateB - dateA;
        });
        setPosts(sortedData.filter((p) => p?.is_global).filter((n) => !n.news));
        sortedData.forEach((p) => {
          if (p.featured_post && p?.is_global) {
            setFeaturedPost(p);
          }
        });
      });
  }, []);


  useEffect(() => {
    if (!searchTerm) {
      setSearchResults([]);
      return;
    }

    const results = posts.filter((post) =>
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags?.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setSearchResults(results);
  }, [searchTerm, posts]);

  function loadMore() {
    setLength((prev) => {
      const current = prev + 6;
      return current > posts.length ? posts.length : current;
    });
  }

  // Static hero — plays once on load.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".blog-heading"));
        gsap
          .timeline({ defaults: { ease: "power4.out" } })
          .from(
            ".blog-intro > *",
            { y: 24, autoAlpha: 0, duration: 0.8, stagger: 0.1 },
            0.3
          )
          .from(".blog-search", { y: 24, autoAlpha: 0, duration: 0.8 }, 0.6)
          .from(
            ".blog-recent-head > *",
            {
              y: 32,
              autoAlpha: 0,
              duration: 0.9,
              stagger: 0.15,
              scrollTrigger: revealTrigger(".blog-recent-head"),
            },
            0
          );
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  // Async content — re-runs whenever the Sanity data or the visible set
  // changes (fetch resolves, Load More, search), so cards rendered later
  // still get their reveal. revertOnUpdate cleans up the previous pass.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        if (featuredPost.id) {
          riseFrom(".blog-featured-media", {
            y: 40,
            duration: 1.1,
            scrollTrigger: revealTrigger(".blog-featured"),
          });
          riseFrom(".blog-featured-copy > *", {
            stagger: 0.15,
            scrollTrigger: revealTrigger(".blog-featured"),
          });
        }
        gsap.utils.toArray(".blog-card").forEach((card) => {
          riseFrom(card, {
            y: 48,
            duration: 1,
            scrollTrigger: revealTrigger(card, "top 90%"),
          });
        });
        const more = pageRef.current.querySelector(".blog-more");
        if (more) {
          riseFrom(more, { scrollTrigger: revealTrigger(more, "top 95%") });
        }
      });
      return () => mm.revert();
    },
    {
      scope: pageRef,
      dependencies: [featuredPost.id, posts.length, length, searchResults.length],
      revertOnUpdate: true,
    }
  );

  // Cursor-following glow, scoped to the "Recent Uploads" section only. The
  // gradient is centered on its home position via percentage transforms, and
  // `x`/`y` add a pixel offset that eases toward the pointer. It returns home
  // when the cursor leaves or the section scrolls out of view.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        const section = recentRef.current;
        const glow = glowRef.current;
        if (!section || !glow) return;

        const FOLLOW = 0.7; // how tightly the glow tracks the pointer

        gsap.set(glow, { xPercent: -50, yPercent: -50 });

        const moveX = gsap.quickTo(glow, "x", {
          duration: 0.7,
          ease: "power3.out",
        });
        const moveY = gsap.quickTo(glow, "y", {
          duration: 0.7,
          ease: "power3.out",
        });

        const onMove = (e) => {
          const rect = section.getBoundingClientRect();
          const homeX = rect.width * 0.5;
          const homeY = rect.height * 0.5;
          moveX((e.clientX - rect.left - homeX) * FOLLOW);
          moveY((e.clientY - rect.top - homeY) * FOLLOW);
        };

        const reset = () => {
          moveX(0);
          moveY(0);
        };

        section.addEventListener("mousemove", onMove);
        section.addEventListener("mouseleave", reset);

        // Snap back to the default position once scrolled past the section.
        const st = ScrollTrigger.create({
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          onLeave: reset,
          onLeaveBack: reset,
        });

        return () => {
          section.removeEventListener("mousemove", onMove);
          section.removeEventListener("mouseleave", reset);
          st.kill();
        };
      });
      return () => mm.revert();
    },
    { scope: recentRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Blog | Analytics Intelligence Ltd — AI, Banking &amp; Fintech Insights</title>
        <link rel="canonical" href="https://analyticsintelligence.com/blog" />
        <meta
          name="description"
          content="Explore the Analytics Intelligence blog for the latest insights on AI for banking, enterprise analytics, predictive analytics for financial services, fintech AI solutions, and data analytics trends in Africa and globally."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence blog, Analytics Intelligence Ltd, artificial intelligence insights, AI for banking blog, enterprise analytics articles, predictive analytics for financial services, fintech AI solutions, data analytics company in Africa, AI infrastructure, business intelligence solutions, AI company in Mauritius, machine learning for banks, AI compliance solutions, regulatory reporting automation, data governance, AI fraud detection"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta property="og:title" content="Blog | Analytics Intelligence Ltd — AI, Banking & Fintech Insights" />
        <meta property="og:description" content="Latest insights on AI for banking, enterprise analytics, fintech AI solutions, and data analytics trends from Analytics Intelligence Ltd." />
        <meta property="og:url" content="https://analyticsintelligence.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://analyticsintelligence.com/favicon.png" />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta name="twitter:title" content="Blog | Analytics Intelligence Ltd — AI, Banking & Fintech Insights" />
        <meta name="twitter:description" content="Latest insights on AI for banking, enterprise analytics, fintech AI solutions, and data analytics trends from Analytics Intelligence Ltd." />
        <meta name="twitter:image" content="https://analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Analytics Intelligence Blog",
            "description": "Insights on AI for banking, enterprise analytics, predictive analytics for financial services, and fintech AI solutions from Analytics Intelligence Ltd.",
            "url": "https://analyticsintelligence.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            }
          }
        `}</script>
      </Helmet>
      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[95vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex flex-col items-center gap-4">
              <h2
                className="blog-heading text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white"
                dangerouslySetInnerHTML={{
                  __html: "Articles, Blogs, and Whitepapers",
                }}
              />
              <div className="blog-intro mb-8">
                <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 max-w-[1200px]">
                  Stay ahead of the curve with thought leadership and deep
                  technical insights. Our content library covers:
                </p>
                <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 max-w-[1200px]">
                  • Advances in AI for analytics and decision science
                </p>
                <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 max-w-[1200px]">
                  • Practical guides on data governance, privacy, and security
                </p>
                <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 max-w-[1200px]">
                  • Whitepapers exploring agentic AI frameworks and enterprise
                  adoption strategies
                </p>
              </div>

              <div className="blog-search relative w-[380px] px-4 md:px-0">
                <div className="absolute left-[44px] md:left-[20px] top-2/4 -translate-y-2/4 text-white md:text-[#888]">
                  <SearchOutlined fontSize="medium" className="text-white" />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pb-2 pl-16 md:pl-12 border text-base bg-[#FFFFFF33] border-[#FFFFFF33] outline-none rounded-full text-white placeholder:text-white"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#010127] relative pt-[48px] pb-0">
          <div className="container relative flex flex-col">
            <div className="blog-featured flex flex-col md:flex-row items-center gap-11">
              <div className="blog-featured-media basis-2/4 flex items-center justify-center">
                <img
                  src={featuredPost?.cover_photo}
                  alt="Data Structures"
                  className="w-full"
                />
              </div>

              {featuredPost.id && (
                <div className="blog-featured-copy basis-2/4 flex flex-col gap-6">
                  <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                      <div className="overflow-hidden rounded-full border-2 border-white">
                        <img
                          src={featuredPost?.author_avatar}
                          alt="avatar"
                          className="w-6 h-6"
                        />
                      </div>
                      <span className="text-white">{featuredPost?.author}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <CalendarTodayOutlinedIcon className="text-white" />
                      <p className="text-white">
                        {new Intl.DateTimeFormat("en-GB", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })
                          .format(new Date(featuredPost?.published_date))
                          .replace(/^(\w+)\s/, "$1, ")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <h2 className="text-2xl md:text-[32px] leading-[1.2] font-bold text-white">
                      {featuredPost?.title}
                    </h2>
                    <p className="text-sm md:text-base text-[#C0C0C0] leading-5">
                      {featuredPost?.body.slice(0, 400)}
                    </p>
                  </div>

                  <Link to={`/blog/${featuredPost.id}`}>
                    <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                      Read More
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        <section ref={recentRef} className="bg-[#010127] relative py-[96px]">
          <div className="container relative flex flex-col items-center gap-11">
            <div
              ref={glowRef}
              className="w-[150px] lg:w-[500px] h-[500px] bg-[#0073FF]/70 blur-[150px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] pointer-events-none will-change-transform"
            ></div>

            <div className="blog-recent-head flex flex-col w-full items-start">
              <h2 className="text-white font-extrabold text-[24px] md:text-[32px] text-left">
                Recent Uploads
              </h2>
              <p className="text-sm md:text-base max-w-[1086px] text-center text-[#C0C0C0]">
                Stay informed with our latest insights
              </p>
            </div>

            <div className="w-full flex gap-[30px] justify-between flex-wrap z-50">
              {searchResults.length < 1 && posts &&
                posts.slice(0, length).map((post) => (
                  <div
                    className="blog-card lg:w-[45%] md:w-[48%] xl:max-w-[400px] w-full mx-auto flex flex-col gap-4 p-6 rounded-xl bg-[#FFFFFF0F] flex-shrink-0"
                    key={post?.id}
                  >
                    <div>
                      <img
                        src={post.cover_photo}
                        alt="blog"
                        className="object-cover h-[200px] w-full rounded-lg"
                      />
                    </div>
                    <div className="flex justify-between">
                      <div className="flex items-center gap-2">
                        <div className="overflow-hidden rounded-full border-2 border-white">
                          <img
                            src={post.author_avatar}
                            alt="avatar"
                            className="w-6 h-6"
                          />
                        </div>
                        <span className="text-white text-sm">
                          {post.author}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <CalendarTodayOutlinedIcon
                          fontSize="small"
                          className="text-white"
                        />
                        <p className="text-white text-sm">
                          {new Intl.DateTimeFormat("en-GB", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                            .format(new Date(post.published_date))
                            .replace(/^(\w+)\s/, "$1, ")}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h1 className="text-white text-[18px] leading-[150%] font-bold">
                        {post.title}
                      </h1>

                      <p className="text-[#C0C0C0] text-sm leading-[150%]">
                        {post.body.slice(0, 100)}......
                      </p>
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-white text-[12px] font-[500] leading-[150%] w-max self-baseline justify-self-end"
                    >
                      <button className="text-sm font-semibold py-1.5 px-4 text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                        Read Now
                      </button>
                    </Link>
                  </div>
                ))}
            </div>
            <div className="w-full flex gap-[30px] justify-between flex-wrap z-50">
              {searchResults.length > 0 && searchResults.map((post) => (
                  <div
                    className="blog-card lg:w-[33%] md:w-[48%] max-w-[400px] w-full mx-auto flex flex-col gap-4 p-6 rounded-xl bg-[#FFFFFF0F] flex-shrink-0"
                    key={post?.id}
                  >
                    <div>
                      <img
                        src={post.cover_photo}
                        alt="blog"
                        className="object-cover h-[200px] w-full rounded-lg"
                      />
                    </div>
                    <div className="flex justify-between">
                      <div className="flex items-center gap-2">
                        <div className="overflow-hidden rounded-full border-2 border-white">
                          <img
                            src={post.author_avatar}
                            alt="avatar"
                            className="w-6 h-6"
                          />
                        </div>
                        <span className="text-white text-sm">
                          {post.author}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <CalendarTodayOutlinedIcon
                          fontSize="small"
                          className="text-white"
                        />
                        <p className="text-white text-sm">
                          {new Intl.DateTimeFormat("en-GB", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                            .format(new Date(post.published_date))
                            .replace(/^(\w+)\s/, "$1, ")}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h1 className="text-white text-[18px] leading-[150%] font-bold">
                        {post.title}
                      </h1>

                      <p className="text-[#C0C0C0] text-sm leading-[150%]">
                        {post.body.slice(0, 100)}......
                      </p>
                    </div>
                    <Link
                      to={`/blog/${post.id}`}
                      className="text-white text-[12px] font-[500] leading-[150%] w-max self-baseline justify-self-end"
                    >
                      <button className="text-sm font-semibold py-1.5 px-4 text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                        Read Now
                      </button>
                    </Link>
                  </div>
                ))}
            </div>

            {searchResults.length < 1 && length < posts.length && (
              <button
                className="blog-more text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2"
                onClick={loadMore}
              >
                Load More
              </button>
            )}
          </div>
        </section>
      </main>
    </>
  );
}
